import Axios from "../utils/axios";
import type { AccountingTotals } from "../pages/Accounting/AccountingStats";
import type { IAccountingFees } from "../interfaces/models/IAccountingFees";
import { parseFeesDocToInvoiceFees } from "../utils/accountingFeesCalculations";
import {
  DEFAULT_SPECIAL_FUND_NAME,
  DEFAULT_SPECIAL_FUND_PERCENTAGE,
} from "../utils/accountingSpecialFund";
import { DEFAULT_ANNUAL_MEETING_REG_FEE } from "../utils/accountingAnnualMeetingFee";
import {
  resolveInvoiceCategory,
  resolveItemCategory,
} from "../utils/billingCategoryUtils";
import {
  deriveBillingStatus,
  isCancelledBillingDoc,
} from "../utils/billingStatus";

const axios = new Axios();

export type FirmBillingTotals = AccountingTotals;

export interface FirmBillingAggregate {
  totals: FirmBillingTotals;
  invoiceDate: string;
  billingPeriod: string;
  status: string;
  comments: string;
}

export type FirmBillingMap = Record<string, FirmBillingAggregate>;

export type OfficeBillingTotals = AccountingTotals;

export interface OfficeBillingAggregate {
  totals: OfficeBillingTotals;
  invoiceDate: string;
  billingPeriod: string;
  status: string;
  comments: string;
}

export type OfficeBillingMap = Record<string, OfficeBillingAggregate>;

interface RawInvoice {
  firmId?: string | null;
  officeId?: string | null;
  partyId?: string;
  partyType?: string;
  context?: { kind?: string; fiscalYear?: number };
  items?: {
    code?: string;
    description?: string;
    amount?: number;
    quantity?: number;
    paid?: number;
  }[];
  total?: number;
  totalPaid?: number;
  issueDate?: string;
  dueDate?: string;
  memo?: string;
  status?: string;
}

interface WorkingAggregate {
  totals: FirmBillingTotals;
  earliestIssue: string;
  billingPeriod: string;
  latestMemo: string;
  latestMemoDate: string;
  anyUnpaidPastDue: boolean;
}

function emptyTotals(): FirmBillingTotals {
  return {
    initiation: { invoiced: 0, paid: 0 },
    annualDues: { invoiced: 0, paid: 0 },
    assessmentDues: { invoiced: 0, paid: 0 },
    membershipBalance: { invoiced: 0, paid: 0 },
    specialFund: { invoiced: 0, paid: 0 },
    annualMeeting: { invoiced: 0, paid: 0 },
    lateFees: { invoiced: 0, paid: 0 },
  };
}

function newWorking(): WorkingAggregate {
  return {
    totals: emptyTotals(),
    earliestIssue: "",
    billingPeriod: "",
    latestMemo: "",
    latestMemoDate: "",
    anyUnpaidPastDue: false,
  };
}

function accumulate(w: WorkingAggregate, inv: RawInvoice, nowMs: number): void {
  if (isCancelledBillingDoc(inv)) {
    return;
  }

  const total = Number(inv.total) || 0;
  const paid = Number(inv.totalPaid) || 0;
  const items = Array.isArray(inv.items) ? inv.items : [];

  if (items.length === 0) {
    const cat = resolveInvoiceCategory(inv);
    if (cat) {
      w.totals[cat].invoiced += total;
      w.totals[cat].paid += paid;
    }
  } else {
    const hasItemPaid = items.some((it) => it.paid != null);
    for (const it of items) {
      const amount = (Number(it.amount) || 0) * (Number(it.quantity) || 1);
      const cat = resolveItemCategory(it, inv);
      if (!cat) {
        continue;
      }
      w.totals[cat].invoiced += amount;
      w.totals[cat].paid += hasItemPaid
        ? Number(it.paid) || 0
        : total > 0
          ? paid * (amount / total)
          : 0;
    }
  }

  const issue = inv.issueDate ?? "";
  if (issue && (!w.earliestIssue || issue < w.earliestIssue)) {
    w.earliestIssue = issue;
    w.billingPeriod = inv.dueDate ?? "";
  }
  if (!w.billingPeriod && inv.dueDate) {
    w.billingPeriod = inv.dueDate;
  }

  const memo = (inv.memo ?? "").trim();
  if (memo && issue >= w.latestMemoDate) {
    w.latestMemo = memo;
    w.latestMemoDate = issue;
  }

  const balance = total - paid;
  if (balance > 0.005 && inv.dueDate) {
    const due = new Date(`${inv.dueDate}T00:00:00.000Z`).getTime();
    if (!Number.isNaN(due) && due < nowMs) {
      w.anyUnpaidPastDue = true;
    }
  }
}

function finalize(w: WorkingAggregate): FirmBillingAggregate {
  const invoiced =
    w.totals.initiation.invoiced +
    w.totals.annualDues.invoiced +
    w.totals.assessmentDues.invoiced +
    w.totals.membershipBalance.invoiced +
    w.totals.specialFund.invoiced +
    w.totals.annualMeeting.invoiced +
    w.totals.lateFees.invoiced;
  const paid =
    w.totals.initiation.paid +
    w.totals.annualDues.paid +
    w.totals.assessmentDues.paid +
    w.totals.membershipBalance.paid +
    w.totals.specialFund.paid +
    w.totals.annualMeeting.paid +
    w.totals.lateFees.paid;

  let status = "";
  if (invoiced > 0) {
    status = deriveBillingStatus(invoiced, paid);
  }

  return {
    totals: w.totals,
    invoiceDate: w.earliestIssue,
    billingPeriod: w.billingPeriod,
    status,
    comments: w.latestMemo,
  };
}

export const getInvoiceAggregatesByFirm = async (
  fiscalYear?: number,
): Promise<FirmBillingMap> => {
  try {
    const params = new URLSearchParams();
    if (fiscalYear != null) {
      params.append(
        "filter",
        JSON.stringify({
          "context.fiscalYear": { value: fiscalYear, comparisonType: "EQUAL" },
        }),
      );
    }
    params.append(
      "fields",
      JSON.stringify([
        "firmId",
        "partyId",
        "partyType",
        "context",
        "items",
        "total",
        "totalPaid",
        "issueDate",
        "dueDate",
        "memo",
        "status",
      ]),
    );
    const qs = params.toString();
    const response = await axios.Get(qs ? `/invoices?${qs}` : "/invoices");
    const rows: RawInvoice[] = Array.isArray(response.data)
      ? response.data
      : [];

    const nowMs = Date.now();
    const working: Record<string, WorkingAggregate> = {};
    for (const inv of rows) {
      if (inv.partyType && inv.partyType !== "firm") {
        continue;
      }
      const firmId = inv.firmId ?? inv.partyId;
      if (!firmId) {
        continue;
      }
      if (!working[firmId]) {
        working[firmId] = newWorking();
      }
      accumulate(working[firmId], inv, nowMs);
    }

    const map: FirmBillingMap = {};
    for (const [firmId, w] of Object.entries(working)) {
      map[firmId] = finalize(w);
    }
    return map;
  } catch {
    return {};
  }
};

export const getInvoiceAggregatesByOffice = async (
  fiscalYear?: number,
): Promise<OfficeBillingMap> => {
  try {
    const params = new URLSearchParams();
    if (fiscalYear != null) {
      params.append(
        "filter",
        JSON.stringify({
          "context.fiscalYear": { value: fiscalYear, comparisonType: "EQUAL" },
        }),
      );
    }
    params.append(
      "fields",
      JSON.stringify([
        "officeId",
        "partyId",
        "partyType",
        "context",
        "items",
        "total",
        "totalPaid",
        "issueDate",
        "dueDate",
        "memo",
        "status",
      ]),
    );
    const qs = params.toString();
    const response = await axios.Get(qs ? `/invoices?${qs}` : "/invoices");
    const rows: RawInvoice[] = Array.isArray(response.data)
      ? response.data
      : [];

    const nowMs = Date.now();
    const working: Record<string, WorkingAggregate> = {};
    for (const inv of rows) {
      if (inv.partyType && inv.partyType !== "office") {
        continue;
      }
      const officeId = inv.officeId ?? inv.partyId;
      if (!officeId) {
        continue;
      }
      if (!working[officeId]) {
        working[officeId] = newWorking();
      }
      accumulate(working[officeId], inv, nowMs);
    }

    const map: OfficeBillingMap = {};
    for (const [officeId, w] of Object.entries(working)) {
      map[officeId] = finalize(w);
    }
    return map;
  } catch {
    return {};
  }
};

export interface BillingHistoryDoc {
  id: string;
  type: "invoice" | "payment";
  partyKey?: string;
  partyId?: string;
  firmId?: string | null;
  currency?: string;
  memo?: string;
  coverLetterBody?: string;
  invoiceNumber?: string;
  context?: { kind?: string; fiscalYear?: number; eventId?: string | null };
  issueDate?: string;
  dueDate?: string;
  items?: {
    code?: string;
    description?: string;
    amount?: number;
    quantity?: number;
    paid?: number;
  }[];
  total?: number;
  totalPaid?: number;
  balance?: number;
  status?: string;
  creditApplied?: number;
  isSent?: boolean;
  timesSent?: number;
  mergedWithAnnualInvoiceId?: string;
  mergedWithAnnualInvoiceNumber?: string;
  mergedAt?: string;
  amount?: number;
  method?: string;
  code?: string;
  description?: string;
  reference?: string;
  paymentDate?: string;
  allocations?: { invoiceId: string; amount: number; category?: string }[];
}

export const getFirmBillingHistory = async (
  firmId: string,
): Promise<BillingHistoryDoc[]> => {
  if (!firmId) {
    return [];
  }
  try {
    const params = new URLSearchParams();
    params.append(
      "filter",
      JSON.stringify({
        partyKey: { value: `firm:${firmId}`, comparisonType: "EQUAL" },
        type: { value: ["invoice", "payment"], comparisonType: "IN" },
      }),
    );
    const response = await axios.Get(`/billing?${params.toString()}`);
    return Array.isArray(response.data)
      ? (response.data as BillingHistoryDoc[])
      : [];
  } catch {
    return [];
  }
};

export const getOfficeBillingHistory = async (
  officeId: string,
): Promise<BillingHistoryDoc[]> => {
  if (!officeId) {
    return [];
  }
  try {
    const params = new URLSearchParams();
    params.append(
      "filter",
      JSON.stringify({
        partyKey: { value: `office:${officeId}`, comparisonType: "EQUAL" },
        type: { value: ["invoice", "payment"], comparisonType: "IN" },
      }),
    );
    const response = await axios.Get(`/billing?${params.toString()}`);
    return Array.isArray(response.data)
      ? (response.data as BillingHistoryDoc[])
      : [];
  } catch {
    return [];
  }
};

export interface NewInvoiceItem {
  code: string;
  description: string;
  amount: number;
  quantity?: number;
}

export interface NewInvoicePayload {
  id?: string;
  type: "invoice";
  partyKey: string;
  partyType: "firm" | "office" | "person";
  partyId: string;
  firmId?: string | null;
  officeId?: string | null;
  currency: string;
  invoiceNumber?: string;
  context: { kind: string; fiscalYear?: number; eventId?: string | null };
  issueDate: string;
  dueDate?: string;
  items: NewInvoiceItem[];
  total: number;
  totalPaid: number;
  balance: number;
  status: string;
  creditApplied?: number;
  memo?: string;
  coverLetterBody?: string;
  /** Number of times this invoice has been sent (reminders). Defaults to 0. */
  timesSent?: number;
}

export const createInvoice = async (
  payload: NewInvoicePayload,
): Promise<unknown> => {
  const response = await axios.Post("/invoices", payload);
  return response.data;
};

export interface FirmInvoiceDoc {
  id: string;
  type: "invoice";
  partyKey?: string;
  partyType?: string;
  partyId?: string;
  firmId?: string | null;
  officeId?: string | null;
  currency?: string;
  invoiceNumber?: string;
  context?: { kind?: string; fiscalYear?: number; eventId?: string | null };
  issueDate?: string;
  dueDate?: string;
  items?: {
    code?: string;
    description?: string;
    amount?: number;
    quantity?: number;
    paid?: number;
  }[];
  total?: number;
  totalPaid?: number;
  balance?: number;
  status?: string;
  memo?: string;
  coverLetterBody?: string;
  isSent?: boolean;
  timesSent?: number;
  [key: string]: unknown;
}

export type OfficeInvoiceDoc = FirmInvoiceDoc;

export const getFirmInvoices = async (
  firmId: string,
): Promise<FirmInvoiceDoc[]> => {
  if (!firmId) {
    return [];
  }
  try {
    const params = new URLSearchParams();
    params.append(
      "filter",
      JSON.stringify({
        partyKey: { value: `firm:${firmId}`, comparisonType: "EQUAL" },
      }),
    );
    const response = await axios.Get(`/invoices?${params.toString()}`);
    return Array.isArray(response.data)
      ? (response.data as FirmInvoiceDoc[])
      : [];
  } catch {
    return [];
  }
};

export const getOfficeInvoices = async (
  officeId: string,
): Promise<OfficeInvoiceDoc[]> => {
  if (!officeId) {
    return [];
  }
  try {
    const params = new URLSearchParams();
    params.append(
      "filter",
      JSON.stringify({
        partyKey: { value: `office:${officeId}`, comparisonType: "EQUAL" },
      }),
    );
    const response = await axios.Get(`/invoices?${params.toString()}`);
    return Array.isArray(response.data)
      ? (response.data as OfficeInvoiceDoc[])
      : [];
  } catch {
    return [];
  }
};

export interface NewPaymentPayload {
  id?: string;
  type: "payment";
  partyKey: string;
  partyType: "firm" | "office" | "person";
  partyId: string;
  currency: string;
  amount: number;
  method: string;
  code?: string;
  description?: string;
  reference?: string;
  paymentDate: string;
  allocations: { invoiceId: string; amount: number; category?: string }[];
  memo?: string;
}

export const createPayment = async (
  payload: NewPaymentPayload,
): Promise<unknown> => {
  const response = await axios.Post("/billing", payload);
  return response.data;
};

export const upsertInvoice = async (
  invoice: FirmInvoiceDoc,
): Promise<unknown> => {
  const response = await axios.Put("/billing", invoice);
  return response.data;
};

export const upsertPayment = async (
  payment: BillingHistoryDoc,
): Promise<unknown> => {
  const response = await axios.Put("/billing", payment);
  return response.data;
};

export interface BalanceAdjustmentDoc {
  id: string;
  type: "balanceAdjustment";
  partyKey: string;
  partyType: "office";
  partyId: string;
  officeId?: string | null;
  firmId?: string | null;
  currency: string;
  amount: number;
  balanceDelta: number;
  kind: string;
  reasonCode?: string;
  notes?: string;
  sourceLabel?: string;
  performedAt: string;
  performedById: string;
  performedByName: string;
  invoiceId?: string;
  paymentId?: string;
  memo?: string;
}

export interface NewBalanceAdjustmentPayload {
  id: string;
  type: "balanceAdjustment";
  partyKey: string;
  partyType: "office";
  partyId: string;
  officeId: string;
  firmId?: string | null;
  currency: string;
  amount: number;
  balanceDelta: number;
  kind: string;
  reasonCode?: string;
  notes?: string;
  sourceLabel?: string;
  performedAt: string;
  performedById: string;
  performedByName: string;
  invoiceId?: string;
  paymentId?: string;
}

export const getOfficeBalanceAdjustments = async (
  officeId: string,
): Promise<BalanceAdjustmentDoc[]> => {
  if (!officeId) {
    return [];
  }
  try {
    const params = new URLSearchParams();
    params.append(
      "filter",
      JSON.stringify({
        partyKey: { value: `office:${officeId}`, comparisonType: "EQUAL" },
        type: { value: "balanceAdjustment", comparisonType: "EQUAL" },
      }),
    );
    const response = await axios.Get(`/billing?${params.toString()}`);
    return Array.isArray(response.data)
      ? (response.data as BalanceAdjustmentDoc[])
      : [];
  } catch {
    return [];
  }
};

export const createBalanceAdjustment = async (
  payload: NewBalanceAdjustmentPayload,
): Promise<unknown> => {
  const response = await axios.Post("/billing", payload);
  return response.data;
};

export interface BillingTemplateVars {
  fullname?: string;
  company?: string;
  jurisdiction?: string;
  category?: string;
  fromToMonthYear?: string;
  totalAmount?: string;
  invoiceNum?: string;
  firmId?: string;
  amount?: string;
  invoiceAmount?: string;
  /** Fiscal year label — "2025-2026" for annual reminders, "2026" for assessment reminders. */
  fiscalYear?: string;
  /** CC list text shown in the annual reminder body. Leave blank to suppress. */
  ccList?: string;
}

export interface SendBillingDocumentPayload {
  to: string | string[];
  cc?: string;
  /** When true, the backend skips internal env BCC. */
  isTest?: boolean;
  subject: string;
  /** Pre-built HTML body — used when kind is not provided. */
  html?: string;
  /**
   * Server loads system catalog HTML (also written on the email log):
   * annual | annual-reminder | assessment | assessment-reminder | payment | payment-partial
   */
  kind?: string;
  templateVars?: BillingTemplateVars;
  filename: string;
  /** Base64-encoded PDF (without the data URL prefix). */
  pdfBase64: string;
}

export const sendBillingDocumentEmail = async (
  payload: SendBillingDocumentPayload,
): Promise<unknown> => {
  const response = await axios.Post("/billing/send", payload);
  return response.data;
};

export interface DuesTier {
  range: string;
  fee: number;
  label?: string;
}

export interface AccountingFeesForYear {
  year: number | null;
  duesTiers: DuesTier[];
  initiationByRange: Record<string, number>;
  defaultTierRange: string;
  isInvestmentBanking: boolean;
  specialFundName: string;
  specialFundPercentage: number;
  annualMeetingRegFee: number;
}

interface RawFeeDetail {
  numberOfProfessionals?: string | null;
  investmentBanking?: string | null;
  fee?: number;
  type?: string;
}

interface RawFeesDoc {
  year?: number;
  type?: string;
  details?: RawFeeDetail[];
}

type FirmFeeContext = {
  serviceType?: unknown;
  firmProfessionalsInPrimary?: string | number | null;
  professionalsInPrimary?: string | number | null;
  countOfPeople?: string | number | null;
  dateJoined?: Date | string | null;
};

export const getAccountingFeesForYear = async (
  year: number,
  firm: FirmFeeContext,
): Promise<AccountingFeesForYear> => {
  const empty: AccountingFeesForYear = {
    year: null,
    duesTiers: [],
    initiationByRange: {},
    defaultTierRange: "",
    isInvestmentBanking: false,
    specialFundName: DEFAULT_SPECIAL_FUND_NAME,
    specialFundPercentage: DEFAULT_SPECIAL_FUND_PERCENTAGE,
    annualMeetingRegFee: DEFAULT_ANNUAL_MEETING_REG_FEE,
  };
  try {
    const response = await axios.Get("/accountingFees");
    const docs: RawFeesDoc[] = Array.isArray(response.data)
      ? response.data
      : [];
    const normalDocs = docs.filter((d) => (d.type ?? "normal") === "normal");
    if (normalDocs.length === 0) {
      return empty;
    }

    const exact = normalDocs.find((d) => d.year === year);
    const fallback = normalDocs
      .slice()
      .sort((a, b) => (b.year ?? 0) - (a.year ?? 0))[0];
    const doc = exact ?? fallback;
    if (!doc) {
      return empty;
    }

    const parsed = parseFeesDocToInvoiceFees(firm, doc as IAccountingFees);

    return {
      year: parsed.year,
      duesTiers: parsed.duesTiers.map((tier) => ({
        range: tier.id,
        fee: tier.fee,
        label: tier.label,
      })),
      initiationByRange: parsed.initiationByRange,
      defaultTierRange: parsed.defaultTierId,
      isInvestmentBanking: parsed.isInvestmentBanking,
      specialFundName: parsed.specialFundName,
      specialFundPercentage: parsed.specialFundPercentage,
      annualMeetingRegFee: parsed.annualMeetingRegFee,
    };
  } catch {
    return empty;
  }
};

export const sumFirmBillingTotals = (
  map: FirmBillingMap,
  firmIds: string[],
): FirmBillingTotals => {
  const acc = emptyTotals();
  for (const id of firmIds) {
    const agg = map[id];
    if (!agg) {
      continue;
    }
    (Object.keys(acc) as (keyof FirmBillingTotals)[]).forEach((key) => {
      acc[key].invoiced += agg.totals[key].invoiced;
      acc[key].paid += agg.totals[key].paid;
    });
  }
  return acc;
};

export const sumOfficeBillingTotals = (
  map: OfficeBillingMap,
  officeIds: string[],
): OfficeBillingTotals => {
  const acc = emptyTotals();
  for (const id of officeIds) {
    const agg = map[id];
    if (!agg) {
      continue;
    }
    (Object.keys(acc) as (keyof OfficeBillingTotals)[]).forEach((key) => {
      acc[key].invoiced += agg.totals[key].invoiced;
      acc[key].paid += agg.totals[key].paid;
    });
  }
  return acc;
};
